import React, { useMemo, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import ChartOptionsSelector from "@/chartOptions/chartOptionsSelector";
import { ChartCard } from "@/components/chart-card";
import { ApiPath } from "@/api/path";
import { LAST_24_HOURS, LAST_7_DAYS } from "@/dateRanges/dateRanges";
import { AUTO } from "@/dateRanges/windowSizes";
import { ONE_MINUTE as ONE_MINUTE_REFRESH_INTERVAL } from "@/dateRanges/refreshIntervals";
import {
  CurrentClampMapping,
  getLatestSensorKitPeriod,
  installationHasDirectEquipmentPowerMonitoring,
  InstallationData,
  isCapture,
  isRefrigerant,
} from "@/installation/installation";

// Map clamp input letter to per-phase TSMetrics.
const CLAMP_TO_CURRENT: Record<string, TSMetric> = {
  A: TSMetric.PHASE_A_CURRENT,
  B: TSMetric.PHASE_B_CURRENT,
  C: TSMetric.PHASE_C_CURRENT,
};

const CLAMP_TO_VOLTAGE: Record<string, TSMetric> = {
  A: TSMetric.PHASE_A_VOLTAGE,
  B: TSMetric.PHASE_B_VOLTAGE,
  C: TSMetric.PHASE_C_VOLTAGE,
};

/**
 * Resolve the TSMetrics to query for a given measurement type on a per-component basis.
 *
 * When a clamp mapping is configured, only component-scoped placements are used —
 * equipment-scoped placements belong to the equipment-level power chart, not here.
 * When no mapping is configured (null), fall back to phase_system_type.
 */
function componentPhasedMetrics(
  mapping: CurrentClampMapping | null | undefined,
  clampMap: Record<string, TSMetric>,
  fallback: TSMetric[],
): TSMetric[] {
  if (!mapping) return fallback;
  const metrics = mapping.placements
    .filter((p) => p.scope === "component")
    .map((p) => clampMap[p.clamp_input])
    .filter(Boolean) as TSMetric[];
  return metrics.length > 0 ? metrics : fallback;
}
import { TSMetric } from "@/query/query";
import { useDateRange } from "@/dateRanges/DateRangeContext";
import { CalendarDateRangePicker } from "@/components/date-range-picker";
import { DatumCardQuery } from "@/components/datum-card-query";
import { CollapsibleCard } from "@/opportunity/opportunity_detail_page/CollapsibleCard";

interface ChartConfig {
  title: string;
  unit: string;
  yAxisName?: string;
  className?: string;
  height?: number;
  canShowTemperatureOnPrimaryYAxis?: boolean;
  clampYAxisToData?: boolean;
  target_unit?: string;
  apiPath?: string;
  metrics?: TSMetric[];
}

interface DatumQueryConfig {
  title: string;
  apiPath: string;
  suffix?: string;
  suffixFn?: (res: any) => string;
  text: string;
  accessorFn: (res: any) => any;
}

const REFRIGERANT_CHARTS = (selectedAsset: InstallationData): ChartConfig[] => {
  const latestPeriod = getLatestSensorKitPeriod(selectedAsset);
  const clampMapping = latestPeriod?.current_clamp_mapping;

  // When the sensor kit's clamps are equipment-scoped (measuring the whole equipment's
  // feed rather than just this component), per-component power and voltage are unreliable.
  // Power is invalid because the phase-angle computation assumes a single known load.
  // Voltage is not representative of this component in isolation.
  // Only current is valid in this case. Null mapping means unconfigured — default to showing all.
  const directEquipmentPower = installationHasDirectEquipmentPowerMonitoring(selectedAsset);

  // Fallback phase selections when no clamp mapping is configured.
  const singlePhase = selectedAsset.phase_system_type === "single-phase";
  const fallbackCurrentMetrics = singlePhase ? [TSMetric.PHASE_A_CURRENT] : [TSMetric.ALL_PHASE_CURRENTS];
  const fallbackVoltageMetrics = singlePhase ? [TSMetric.PHASE_A_VOLTAGE] : [TSMetric.ALL_PHASE_VOLTAGES];

  // Per-component charts always use component-scoped placements.
  // Equipment-scoped placements belong to the equipment-level total power chart, not here.
  const currentMetrics = componentPhasedMetrics(clampMapping, CLAMP_TO_CURRENT, fallbackCurrentMetrics);
  const voltageMetrics = componentPhasedMetrics(clampMapping, CLAMP_TO_VOLTAGE, fallbackVoltageMetrics);

  return [
    {
      title: "Refrigerant loop temperatures",
      metrics: [TSMetric.ALL_TEMPERATURES],
      unit: "\u00B0F",
      yAxisName: "Temperature",
      className: "col-span-12 flex-1 lg:col-span-6",
      height: 450,
      canShowTemperatureOnPrimaryYAxis: true,
      clampYAxisToData: true,
      target_unit: "degF"
    },
    ...(!directEquipmentPower ? [{
      title: "Power",
      apiPath: ApiPath.INSTALLATION_REFRIGERANT_CONTINUOUS_ACTIVE_POWER,
      unit: "watts",
      yAxisName: "Active power"
    }] : []),
    ...(!directEquipmentPower ? [{
      title: "Voltage",
      metrics: voltageMetrics,
      unit: "volts",
      yAxisName: "Voltage"
    }] : []),
    {
      title: "Current",
      metrics: currentMetrics,
      unit: "amps",
      yAxisName: "Current"
    },
  ];
};

const CAPTURE_DATUM_QUERIES: DatumQueryConfig[] = [
  {
    title: "CO\u2082 Captured",
    apiPath: ApiPath.INSTALLATION_TOTAL_KG_CAPTURED,
    suffixFn: (res) => res?.suffix ?? "",
    text: "Total amount removed from the building's air over this time period",
    accessorFn: (res: any) => res
  },
  {
    title: "Maximum Rate",
    apiPath: ApiPath.INSTALLATION_TREE_EQUIV_AND_MAX_RATE,
    suffix: "g/hr",
    text: "Maximum capture rate of the Thalo system for this time period",
    accessorFn: (res: any) => res?.datums.find((item: any) => item.name == "max_rate")
  },
  {
    title: "Tree equivalent",
    apiPath: ApiPath.INSTALLATION_TREE_EQUIV_AND_MAX_RATE,
    suffix: "trees",
    text: "Approximate number of trees needed to capture at max rate",
    accessorFn: (res: any) => res?.datums.find((item: any) => item.name == "tree_equiv")
  }
];

export const RefrigerantAssetCharts = ({ selectedAsset, selectedOrganizationId }: {
  selectedAsset: InstallationData,
  selectedOrganizationId: number
}) => {

  const { dateRange, windowSize, refreshInterval } = useDateRange({
    overrideDefaultDateRange: LAST_24_HOURS,
    overrideDefaultWindowSize: AUTO,
    overrideDefaultRefreshInterval: ONE_MINUTE_REFRESH_INTERVAL
  })

  const charts = useMemo(() => REFRIGERANT_CHARTS(selectedAsset), [selectedAsset]);

  const latestPeriod = getLatestSensorKitPeriod(selectedAsset);
  const firmwareVersion = latestPeriod?.sensor_kit.config.version;
  const isVersion28x = firmwareVersion?.startsWith("2.8") ?? false;
  const [chartStates, setChartStates] = useState<Record<number, { loading: boolean; hasData: boolean }>>({});

  const makeLoadingCallback = useCallback((index: number) => (loading: boolean) => {
    setChartStates(prev => ({ ...prev, [index]: { ...prev[index], loading } }));
  }, []);

  const makeHasDataCallback = useCallback((index: number) => (hasData: boolean) => {
    setChartStates(prev => ({ ...prev, [index]: { ...prev[index], hasData } }));
  }, []);

  const allDoneLoading = Object.keys(chartStates).length === charts.length &&
    Object.values(chartStates).every(s => !s.loading);
  const noneHaveData = Object.values(chartStates).length > 0 &&
    Object.values(chartStates).every(s => !s.hasData);

  return (
    <CollapsibleCard
      title="Real-time data charts"
      defaultOpen={false}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-1">
          <ChartOptionsSelector showPowerOffToggle={isVersion28x}/>
          <CalendarDateRangePicker/>
        </div>
        {charts.map((config, index) => (
          <ChartCard
            key={`query--chart-${index}`}
            className={config.className}
            title={config.title}
            yAxisName={config.yAxisName}
            height={config.height}
            unit={config.unit}
            apiPath={config.apiPath ?? ApiPath.INSTALLATION_GENERIC_LINE_CHART_QUERY}
            query={[
              {
                metrics: config.metrics,
                windowSize: windowSize,
                dateRange: dateRange,
                organization_id: selectedOrganizationId,
                installation_id: [selectedAsset.id],
                ...(config.target_unit && { target_unit: config.target_unit })
              },
            ]}
            refreshInterval={refreshInterval}
            canShowTemperatureOnPrimaryYAxis={config.canShowTemperatureOnPrimaryYAxis}
            clampYAxisToData={config.clampYAxisToData}
            enableDataGapVisualization={isVersion28x}
            isLoadingCallback={makeLoadingCallback(index)}
            hasDataCallback={makeHasDataCallback(index)}
          />
        ))}
        {allDoneLoading && noneHaveData && (
          <div className="flex items-center justify-center py-8 bg-gray-50 rounded-xl border border-gray-200">
            <p className="text-sm text-gray-400 text-center px-4">
              No real-time data at this time. Make sure the asset is powered on and reporting.
            </p>
          </div>
        )}
      </div>
    </CollapsibleCard>
  );
};

const CaptureAssetCharts = ({ selectedAsset, selectedOrganizationId }: {
  selectedAsset: InstallationData,
  selectedOrganizationId: number
}) => {

  const { dateRange, windowSize, refreshInterval } = useDateRange({
    overrideDefaultDateRange: LAST_7_DAYS,
    overrideDefaultWindowSize: AUTO,
  });

  return (
    <CollapsibleCard title="Real-time data charts" defaultOpen={false}>
      <div className="flex flex-col gap-4">
        <div className="flex justify-end">
          <Card className="p-2 w-full lg:w-fit flex items-center gap-2">
            <ChartOptionsSelector/>
            <CalendarDateRangePicker/>
          </Card>
        </div>
        <ChartCard
          className="col-span-12 flex-1 lg:col-span-6"
          title="CO\u2082 input vs output"
          height={450}
          unit="ppm"
          apiPath={ApiPath.INSTALLATION_GENERIC_LINE_CHART_QUERY}
          query={[
            {
              metrics: [TSMetric.CO2_INPUT, TSMetric.CO2_OUTPUT],
              windowSize: windowSize,
              dateRange: dateRange,
              organization_id: selectedOrganizationId,
              installation_id: [selectedAsset.id],
            },
          ]}
          refreshInterval={refreshInterval}
          clampYAxisToData
        />
        {CAPTURE_DATUM_QUERIES.map((config) => (
          <DatumCardQuery
            key={config.title}
            title={config.title}
            apiPath={config.apiPath}
            suffix={config.suffix}
            suffixFn={config.suffixFn}
            text={config.text}
            accessorFn={config.accessorFn}
            query={{
              installation_id: [selectedAsset.id],
              windowSize,
              dateRange
            }}
            refreshInterval={refreshInterval}
            hideIfNoData={true}
            hideIfZero={true}
          />
        ))}
      </div>
    </CollapsibleCard>
  );
};

interface CombustionChartConfig {
  title: string;
  metrics: TSMetric[];
  unit: string;
}

const COMBUSTION_CHARTS: CombustionChartConfig[] = [
  { title: "CO\u2082 Concentration", metrics: [TSMetric.CO2_CONCENTRATION], unit: "ppm" },
  { title: "CH\u2084 Concentration", metrics: [TSMetric.CH4_CONCENTRATION], unit: "ppm" },
  { title: "O\u2082 Concentration", metrics: [TSMetric.O2_CONCENTRATION], unit: "%" },
  { title: "CO Concentration", metrics: [TSMetric.CO_CONCENTRATION], unit: "ppm" },
  { title: "NO\u2082 Concentration", metrics: [TSMetric.NO2_CONCENTRATION], unit: "ppm" },
  { title: "SO\u2082 Concentration", metrics: [TSMetric.SO2_CONCENTRATION], unit: "ppm" },
  { title: "Particulate 1.0", metrics: [TSMetric.PARTICULATES_PM1_0], unit: "\u00B5g/m\u00B3" },
  { title: "Particulate 2.5", metrics: [TSMetric.PARTICULATES_PM2_5], unit: "\u00B5g/m\u00B3" },
  { title: "Particulate 10", metrics: [TSMetric.PARTICULATES_PM10], unit: "\u00B5g/m\u00B3" },
  { title: "VOCs", metrics: [TSMetric.HCHO_CONCENTRATION], unit: "ppb" },
];

const CombustionAssetCharts = ({ selectedAsset }: {
  selectedAsset: InstallationData
}) => {
  const { dateRange, windowSize, refreshInterval } = useDateRange({
    overrideDefaultDateRange: LAST_24_HOURS,
    overrideDefaultWindowSize: AUTO,
  });

  return (
    <CollapsibleCard title="Real-time data charts" defaultOpen={false}>
      <div className="flex flex-col gap-4">
        <div className="flex justify-end">
          <Card className="p-2 w-full lg:w-fit flex items-center gap-2">
            <ChartOptionsSelector/>
            <CalendarDateRangePicker/>
          </Card>
        </div>
        {COMBUSTION_CHARTS.map((config) => (
          <ChartCard
            key={`${config.metrics.join("-")}-installation_id-${selectedAsset.id}`}
            className="col-span-12 flex-1 lg:col-span-6"
            title={config.title}
            height={450}
            unit={config.unit}
            apiPath={ApiPath.INSTALLATION_GENERIC_LINE_CHART_QUERY}
            query={[
              {
                installation_id: [selectedAsset.id],
                windowSize,
                dateRange,
                metrics: config.metrics,
              },
            ]}
            refreshInterval={refreshInterval}
          />
        ))}
      </div>
    </CollapsibleCard>
  )
}

export const getAssetTypeCharts = (
  selectedAsset: InstallationData,
  selectedOrganizationId: number
): React.ReactNode => {
  if (isRefrigerant(selectedAsset)) {
    return <RefrigerantAssetCharts selectedAsset={selectedAsset} selectedOrganizationId={selectedOrganizationId} />;
  }
  if (isCapture(selectedAsset)) {
    return <CaptureAssetCharts selectedAsset={selectedAsset} selectedOrganizationId={selectedOrganizationId} />;
  }
  return <CombustionAssetCharts selectedAsset={selectedAsset} />;
};
